import { Button, Stack } from "@mantine/core"
import { useForm } from "@mantine/form"
import { assignmentFeePayTypeOptions, interestTreatmentOptions, strBooleanOptions, tradeAssociationOptions, tradeDocsTypeOptions, tradePaymentTypeOptions, tradeTypeOptions, transferTypeOptions } from "app/models/dropdown-options"
import { MasterTradeParams } from "app/models/master-trade-params"
import { editTermsOfTradeDetails } from "app/state/ducks/master-trades/thunks"
import { getSubTrades } from "app/state/ducks/sub-trades/selectors"
import BlackSelectInput from "app/views/components/inputs/BlackSelectInput"
import { ErrorNotification, SuccessNotification } from "app/views/components/notifications/notification"
import WarningText from "app/views/components/warning-text/warning-text"
import { useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

type Params = {
    trade?: MasterTradeParams
    closeModal: () => void
    onHandleSubmit?: any
}
export default function EditTermsOfTradeForm({
    trade,
    closeModal,
    onHandleSubmit
}: Params) {
    const dispatch = useDispatch()
    const [isLoading, setIsLoading] = useState(false)
    const subTrades = useSelector(getSubTrades)

    const isSettled = useMemo(() => {
        return subTrades.some(st => st.masterTradeId.id === trade?.id && st.status == "Settled")
    }, [subTrades])


    const form = useForm({
        initialValues: {
            tradeType: trade?.tradeType ?? '',
            tradeAssociation: trade?.tradeAssociation ?? '',
            tradeDocsType: trade?.tradeDocsType,
            transferType: trade?.transferType ?? '',
            interestTreatment: trade?.interestTreatment ?? '',
            assignmentFeePayType: trade?.assignmentFeePayType ?? '',
            paymentType: trade?.paymentType ?? '',
            disableDCCoC: trade?.disableDCCoC ? 'true' : 'false',
        }
    })
    async function onSubmit(totDetails: any) {
        try {
            setIsLoading(true)
            const response: any = await dispatch(editTermsOfTradeDetails(totDetails, trade))
            if (response.success) {
                SuccessNotification({
                    title: 'Successful Terms of Trade Details Saved',
                    message: 'You edited an Terms of Trade details for open Trade',
                })
            } else {
                ErrorNotification({
                    title: 'Terms of Trade details save failed',
                    message: response.payload ?? 'Check data and try again',
                })
            }
            return undefined
        } catch {
            ErrorNotification({
                title: 'Terms of Trade Details save failed',
                message: 'Check data and try again',
            })
        } finally {
            setIsLoading(false)
        }
    }

    async function submitForm(event: any) {
        event.preventDefault()
        if (!form.validate().hasErrors) {
            const formValues = form.getTransformedValues()
            // Convert string boolean back to boolean for disableDCCoC
            const transformedValues = {
                ...formValues,
                disableDCCoC: formValues.disableDCCoC === 'true'
            }
            const trade = await onSubmit(transformedValues)
            onHandleSubmit(trade ?? formValues)
        } else {
            ErrorNotification({
                title: 'Form Validation Error',
                message: 'Please fill in required fields',
            });
        }
    }
    return (
        <form onSubmit={submitForm} className="stepper-form">
            <Stack spacing="12px !important">
                <div className="edit-trade-input-field-tuple">
                    <BlackSelectInput disabled={isSettled} label="Trade Type" data={tradeTypeOptions} {...form.getInputProps("tradeType")} />
                    <BlackSelectInput disabled={isSettled} label="Trade Association" data={tradeAssociationOptions} {...form.getInputProps("tradeAssociation")} />
                </div>
                <div className="edit-trade-input-field-tuple">
                    <BlackSelectInput disabled={isSettled} label="Trade Docs" data={tradeDocsTypeOptions} {...form.getInputProps("tradeDocsType")} />
                    <BlackSelectInput disabled={isSettled} label="Transfer Type" data={transferTypeOptions} {...form.getInputProps("transferType")} />
                </div>

                <div className="edit-trade-input-field-tuple">
                    <BlackSelectInput disabled={isSettled} label="Interest Treatment" data={interestTreatmentOptions} {...form.getInputProps("interestTreatment")} />
                    <BlackSelectInput disabled={isSettled} label="Assignment Fee Split" data={assignmentFeePayTypeOptions} {...form.getInputProps("assignmentFeePayType")} />
                </div>

                <div className="edit-trade-input-field-tuple">
                    <BlackSelectInput disabled={isSettled} label="Payment Method" data={tradePaymentTypeOptions} {...form.getInputProps("paymentType")} />
                    <BlackSelectInput disabled={isSettled} label="Disable DC/CoC" data={strBooleanOptions} {...form.getInputProps("disableDCCoC")} />
                </div>

                {isSettled && <WarningText text="Trade has settled subtrades" />}

                <div className="action-buttons-block">
                    <Button className="cancel-button" onClick={() => closeModal()}>Cancel</Button>

                    <Button
                        type="submit"
                        loading={isLoading}
                        disabled={!form.isValid()}
                        className="delete-button stratch-delete-button"
                    >
                        Save
                    </Button>
                </div>
            </Stack>
        </form >
    )
}
